import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CompositeFavicon } from "@/components/ui/composite-favicon";
import { ArrowLeft, Radio } from "lucide-react";
import type { Station } from "@shared/schema";

interface StationToolLayoutProps {
  station: Station;
  children: React.ReactNode;
}

export function StationToolLayout({ station, children }: StationToolLayoutProps) {
  const [location] = useLocation();
  
  // Determine active tool from URL
  const activeTool = location.includes('/copy') ? 'copy' : 'vbr';
  
  // Check which features are enabled (default to true for backwards compatibility)
  const vbrEnabled = station.vbrEngineEnabled ?? true;
  const copyEnabled = station.copyEngineEnabled ?? true;
  
  // Count enabled features for grid layout
  const enabledCount = (vbrEnabled ? 1 : 0) + (copyEnabled ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link href="/dashboard">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
        
        <div className="flex items-center gap-4 mb-4">
          <CompositeFavicon 
            faviconUrls={station.faviconUrls} 
            size="lg"
            fallbackIcon={<Radio className="w-8 h-8 text-primary-600" />}
          />
          <div>
            <h1 className="text-3xl font-bold">{station.name}</h1>
            <p className="text-muted-foreground">AI Tools to Help you Sell Better</p>
          </div>
        </div>

        {/* Tool Navigation Tabs - Only show if at least one feature is enabled */}
        {enabledCount > 0 && (
          <Tabs value={activeTool} className="w-full">
            <TabsList className={`grid w-full max-w-md ${enabledCount === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {vbrEnabled && (
                <TabsTrigger value="vbr" asChild>
                  <Link 
                    href={`/stations/${station.id}/vbr`}
                    className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
                    data-testid={`tab-vbr-${station.id}`}
                  >
                    VBR Engine
                  </Link>
                </TabsTrigger>
              )}
              {copyEnabled && (
                <TabsTrigger value="copy" asChild>
                  <Link 
                    href={`/stations/${station.id}/copy`}
                    className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
                    data-testid={`tab-copy-${station.id}`}
                  >
                    Copy Engine
                  </Link>
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Tool Content */}
      {children}
    </div>
  );
}